import React, { useEffect, useMemo, useState } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { fmtDate } from '../../utils/helpers';
import TaskCreateModal from './TaskCreateModal';
import { projectTrackingServices } from '../../APIServices/projectTrackingServices';
import { usersManagementServices } from '../../APIServices/usersManagementServices';
import { projectMilestoneServices } from '../../APIServices/projectMilestoneServices';

function ProjectTrackingModule({ onNavigate }) {
  const { state, setState, loadStudyList } = useData();
  const toast = useToast();
  const [studyId, setStudyId] = useState('');
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ownerList, setOwnerList] = useState([]);
  const [milestoneMetadataList, setMilestoneMetadataList] = useState([]);

  useEffect(() => {
    if (state.studies.length === 0) {
      loadStudyList?.();
    }

    if (!studyId && state.studies.length > 0) {
      const activeStudy = state.studies.find((s) => s.status === 'active');
      setStudyId(activeStudy?.id || state.studies[0]?.id);
    }
  }, [state.studies, studyId, loadStudyList]);

  const study = state.studies.find((s) => String(s.id || s.studyId) === String(studyId));
  const milestones = useMemo(
    () => state.milestones.filter((m) => m.studyId === studyId).sort((a, b) => new Date(a.plannedAt) - new Date(b.plannedAt)),
    [state.milestones, studyId]
  );
  const tasks = useMemo(() => state.tasks.filter((t) => t.studyId === studyId), [state.tasks, studyId]);

  const getOwnerValue = (user) => user?.user || user?.userId || user?.id || '';

  const getOwnerLabel = (user) => {
    const fullName = [user?.firstName, user?.middleName, user?.lastName].filter(Boolean).join(' ').trim();
    return fullName || user?.name || user?.email || 'Unknown User';
  };

  const getOwnerInitialFromName = (name) => {
    if (!name) return '?';
    const parts = String(name).trim().split(' ').filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  };

  const syncProjectTrackingData = async () => {
    setLoading(true);
    try {
      const [taskData, milestoneData] = await Promise.all([
        projectTrackingServices.getProjectTrackingData('all', '', 1, 1000, { recordType: 'task' }),
        projectTrackingServices.getProjectTrackingData('all', '', 1, 1000, { recordType: 'milestone' }),
      ]);

      const mappedTasks = (taskData?.data || []).map((item) => ({
        id: item.trackingId,
        _id: item._id,
        trackingId: item.trackingId,
        studyId: item.studyId,
        title: item.title,
        projectMilestoneId: item.projectMilestoneId,
        projectMilestoneName: item.projectMilestoneName,
        owner: item.owner,
        dueAt: item.dueAt,
        priority: item.priority || 'medium',
        status: item.status || 'todo',
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      }));

      const mappedMilestones = (milestoneData?.data || []).map((item) => ({
        id: item.trackingId,
        _id: item._id,
        trackingId: item.trackingId,
        studyId: item.studyId,
        title: item.title,
        plannedAt: item.plannedAt,
        endAt: item.endAt,
        actualAt: item.actualAt,
        status: item.status || 'planned',
        progress: item.progress || 0,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      }));

      setState((prev) => ({
        ...prev,
        tasks: mappedTasks,
        milestones: mappedMilestones,
      }));
    } catch (error) {
      toast('Unable to load project tracking data.', 'warn');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    syncProjectTrackingData();
  }, []);

  const loadProjectMilestoneMetadata = async () => {
    try {
      const data = await projectMilestoneServices.getProjectMilestoneData('active', '', 1, 1000);
      setMilestoneMetadataList(data?.data || []);
    } catch (error) {
      setMilestoneMetadataList([]);
    }
  };

  useEffect(() => {
    loadProjectMilestoneMetadata();
  }, []);

  const loadOwners = async () => {
    try {
      let data = await usersManagementServices.getUserData('all', 'all', '', 1, 1000);
      let users = data?.data || [];

      if (!users.length) {
        data = await usersManagementServices.getUserData('all', 'user', '', 1, 1000);
        users = data?.data || [];
      }

      setOwnerList(users);
    } catch (error) {
      setOwnerList([]);
      toast('Unable to load owner list.', 'warn');
    }
  };

  useEffect(() => {
    loadOwners();
  }, []);

  const toggleTaskStatus = async (task, nextStatus) => {
    try {
      await projectTrackingServices.updateProjectTracking({
        id: task._id,
        status: nextStatus,
      });
      toast(`Task updated to ${nextStatus}`, 'ok');
      syncProjectTrackingData();
    } catch (error) {
      toast('Unable to update task status.', 'warn');
    }
  };

  const getOwnerInitials = (ownerId) => {
    const user = ownerList.find((u) => String(getOwnerValue(u)) === String(ownerId));
    return getOwnerInitialFromName(getOwnerLabel(user));
  };

  const getOwnerName = (ownerId) => {
    const user = ownerList.find((u) => String(getOwnerValue(u)) === String(ownerId));
    return getOwnerLabel(user) || 'Unassigned';
  };
  const metadataMilestoneFallback = useMemo(() => {
    if (!studyId || !milestoneMetadataList.length) return [];

    const studyStart = study?.plannedStart
      ? new Date(study.plannedStart)
      : new Date();

    return milestoneMetadataList.map((item, index) => {
      const plannedDate = new Date(studyStart);
      plannedDate.setDate(studyStart.getDate() + index * 14);

      return {
        id: `metadata-${item._id || item.projectMilestoneId || index}`,
        title: item.projectMilestone,
        plannedAt: plannedDate.toISOString(),
        status: item.defaultTimelineStatus || 'planned',
        progress: 0,
        isMetadataFallback: true,
      };
    });
  }, [milestoneMetadataList, study, studyId]);

  const displayMilestones = milestones.length > 0 ? milestones : metadataMilestoneFallback;

  // Helper functions for milestone timeline
  const generateMonthLabels = (milestones, plannedStart, plannedEnd) => {
    if (!milestones.length) return [];
    
    const allDates = milestones.flatMap((m) => [m.plannedAt, m.actualAt].filter(Boolean).map((d) => new Date(d).getTime()));
    if (plannedStart) allDates.push(new Date(plannedStart).getTime());
    if (plannedEnd) allDates.push(new Date(plannedEnd).getTime());

    const minDate = Math.min(...allDates);
    const maxDate = Math.max(...allDates);
    const span = maxDate - minDate;

    const pct = (d) => span > 0 ? (new Date(d).getTime() - minDate) / span * 100 : 0;

    const monthLabels = [];
    const d = new Date(minDate);
    d.setDate(1);
    while (d.getTime() <= maxDate) {
      monthLabels.push({ 
        label: d.toLocaleDateString('en', { month: 'short' }), 
        pct: pct(d.toISOString()) 
      });
      d.setMonth(d.getMonth() + 1);
    }
    return monthLabels;
  };

  const getMilestonePosition = (milestone, allMilestones, plannedStart, plannedEnd) => {
    const allDates = allMilestones.flatMap((m) => [m.plannedAt, m.actualAt].filter(Boolean).map((d) => new Date(d).getTime()));
    if (plannedStart) allDates.push(new Date(plannedStart).getTime());
    if (plannedEnd) allDates.push(new Date(plannedEnd).getTime());

    const minDate = Math.min(...allDates);
    const maxDate = Math.max(...allDates);
    const span = maxDate - minDate;

    const pct = (d) => span > 0 ? (new Date(d).getTime() - minDate) / span * 100 : 0;
    return pct(milestone.plannedAt) * 0.94;
  };

  const getMilestoneWidth = (milestone, allMilestones) => {
    if (milestone.endAt) {
      const allDates = allMilestones.flatMap((m) => [m.plannedAt, m.actualAt].filter(Boolean).map((d) => new Date(d).getTime()));
      const minDate = Math.min(...allDates);
      const maxDate = Math.max(...allDates);
      const span = maxDate - minDate;
      const pct = (d) => span > 0 ? (new Date(d).getTime() - minDate) / span * 100 : 0;
      const start = pct(milestone.plannedAt);
      const end = pct(milestone.endAt);
      return Math.max((end - start) * 0.94, 2);
    }
    return 2.5;
  };

  const getTodayPosition = () => {
    if (!displayMilestones.length) return null;
    const allDates = displayMilestones.flatMap((m) => [m.plannedAt, m.actualAt].filter(Boolean).map((d) => new Date(d).getTime()));
    if (study?.plannedStart) allDates.push(new Date(study.plannedStart).getTime());
    if (study?.plannedEnd) allDates.push(new Date(study.plannedEnd).getTime());

    const minDate = Math.min(...allDates);
    const maxDate = Math.max(...allDates);
    const span = maxDate - minDate;
    const today = Date.now();

    if (today >= minDate && today <= maxDate) {
      const pct = (d) => span > 0 ? (new Date(d).getTime() - minDate) / span * 100 : 0;
      return pct(new Date(today).toISOString());
    }
    return null;
  };

  const monthLabels = generateMonthLabels(displayMilestones, study?.plannedStart || '2026-04-01', study?.plannedEnd || '2026-11-15');
  const todayPosition = getTodayPosition();

  const getStatusColor = (status) => {
    if (status === 'done') return '#0f6e56';
    if (status === 'at_risk') return '#ba7517';
    if (status === 'delayed') return '#a32d2d';
    if (status === 'active') return '#5947b8';
    return '#d4d2cc';
  };

  const getStatusLabel = (status) => {
    if (status === 'done') return 'Done';
    if (status === 'at_risk') return 'At risk';
    if (status === 'delayed') return 'Delayed';
    if (status === 'active') return 'Active';
    return 'Planned';
  };

  const legendItems = [
    { label: 'Done', color: '#0f6e56' },
    { label: 'Active', color: '#5947b8' },
    { label: 'At risk', color: '#ba7517' },
    { label: 'Delayed', color: '#a32d2d' },
    { label: 'Remaining', color: '#7f77dd' },
    { label: 'Planned', color: '#d4d2cc' },
  ];

  const openStudyModule = () => {
    if (typeof onNavigate === 'function') {
      onNavigate('studies');
    }
  };

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Section 5 · Operational view</div>
          <h1 className="page-title">Project tracking</h1>
          <p className="page-sub">Milestones, tasks, communications and progress per study.</p>
        </div>
        <div className="flex gap-2" style={{ flexShrink: 0 }}>
          <select 
            className="btn" 
            value={studyId} 
            onChange={(e) => setStudyId(e.target.value)} 
            style={{ minWidth: 240 }}
          >
            {state.studies.map((s) => (
              <option key={s.id} value={s.id}>{s.code || s.studyCode} · {s.molecule || s.moleculeProduct || s.name || s.code || s.studyCode}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={() => setShowCreateTask(true)}>
            <i className="ti ti-plus" /> Add task
          </button>
        </div>
      </div>

      {!study ? (
        <div className="card card-pad">
          <div className="empty">
            <i className="ti ti-folder-off" />
            <div className="empty-title">No study selected</div>
            <div className="tiny muted">Please select a study from the dropdown above.</div>
          </div>
        </div>
      ) : loading ? (
        <div className="card card-pad" style={{ textAlign: 'center' }}>
          <div className="buttonLoader" />
          <div style={{ marginTop: 12 }}>Loading project tracking...</div>
        </div>
      ) : (
        <>
          {/* Milestone Timeline - Exact Layout from Image */}
          <div className="section-label mb-4">
            <i className="ti ti-timeline-event" /> Milestone timeline
          </div>
          
          <div className="milestone-timeline-card">
            {/* Month Labels Row */}
            <div className="milestone-months-header">
              {monthLabels.map((m, i) => (
                <span key={i} className="milestone-month" style={{ left: `${m.pct}%` }}>
                  {m.label}
                </span>
              ))}
            </div>

            {/* Today Marker */}
            {todayPosition !== null && (
              <>
                <div className="milestone-today-line" style={{ left: `${todayPosition}%` }} />
                <div className="milestone-today-label" style={{ left: `${todayPosition}%` }}>
                  Today · {new Date().toLocaleDateString('en', { day: 'numeric', month: 'short' })}
                </div>
              </>
            )}

            {/* Milestone Rows */}
            <div className="milestone-rows-container">
              {displayMilestones.length === 0 ? (
                <div className="empty" style={{ padding: 16 }}>
                  <i className="ti ti-timeline-off" />
                  <div className="empty-title">No milestones</div>
                  <div className="tiny muted">No milestone metadata found. Please add milestones from admin metadata.</div>
                </div>
              ) : displayMilestones.map((m) => {
                const color = getStatusColor(m.status);
                const label = getStatusLabel(m.status);
                const isDone = m.status === 'done';
                const isActive = m.status === 'at_risk' || m.status === 'active';
                const progress = m.progress || 0;

                const position = getMilestonePosition(m, displayMilestones, study?.plannedStart || '2026-04-01', study?.plannedEnd || '2026-11-15');
                const width = getMilestoneWidth(m, displayMilestones);

                return (
                  <div key={m.id} className="milestone-row-item">
                    <div className="milestone-name-cell">
                      <span className="milestone-name-text">{m.title}</span>
                      {progress > 0 && !isDone && (
                        <span className="milestone-progress-pill">{progress}%</span>
                      )}
                      {m.isMetadataFallback && (
                        <span className="tag tag-default tag-dot tiny" style={{ marginLeft: 8 }}>
                          master
                        </span>
                      )}
                      <span className={`milestone-status-tag milestone-status-${m.status}`}>
                        - {label}
                      </span>
                    </div>
                    <div className="milestone-track-cell">
                      <div 
                        className="milestone-bar-line"
                        style={{
                          left: `${position}%`,
                          width: `${Math.max(width, 0.5)}%`,
                          background: color,
                          opacity: isDone ? 0.7 : 1
                        }}
                      >
                        {isActive && progress > 0 && (
                          <div 
                            className="milestone-bar-progress-fill"
                            style={{ width: `${Math.min(progress, 100)}%` }}
                          />
                        )}
                      </div>
                      {(isActive || isDone) && (
                        <span 
                          className="milestone-bar-status-text"
                          style={{
                            left: `${Math.min(position + 1, 95)}%`,
                            color: color
                          }}
                        >
                          {isDone ? 'Done' : `${label}${progress > 0 ? ` · ${progress}%` : ''}`}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="milestone-legend">
              {legendItems.map((item) => (
                <span className="milestone-legend-item" key={item.label}>
                  <span className="milestone-legend-color" style={{ background: item.color }}></span>
                  {item.label}
                </span>
              ))}
              <span className="milestone-legend-item milestone-legend-today">
                <span className="milestone-legend-today-line"></span>
                Today
              </span>
            </div>
          </div>

          {/* Active Tasks */}
          <div className="section-label mt-4 mb-5">
            <i className="ti ti-list-check" /> Active tasks
          </div>
          <div className="card">
            {tasks.length === 0 ? (
              <div className="empty">
                <i className="ti ti-checkbox" />
                <div className="empty-title">No tasks</div>
                <div className="tiny muted">Add tasks to track operational items.</div>
              </div>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Task</th>
                    <th>Milestone</th>
                    <th>Owner</th>
                    <th>Due</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((t) => {
                    const overdue = t.status !== 'done' && t.dueAt && new Date(t.dueAt) < new Date();
                    const priorityVariant = t.priority === 'critical' ? 'danger' : 
                                           t.priority === 'high' ? 'warn' : 
                                           t.priority === 'low' ? 'info' : 'default';

                    return (
                      <tr
                        key={t.id}
                        className={`clickable-row ${t.status === 'done' ? 'task-done' : ''}`}
                        onClick={openStudyModule}
                      >
                        <td>
                          <div className="flex gap-2 center">
                            <input
                              type="checkbox"
                              checked={t.status === 'done'}
                              onChange={(e) => toggleTaskStatus(t, e.target.checked ? 'done' : 'todo')}
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--accent-3)' }}
                            />
                            <span className={t.status === 'done' ? 'muted' : ''} style={{ textDecoration: t.status === 'done' ? 'line-through' : 'none' }}>
                              {t.title}
                            </span>
                          </div>
                        </td>
                        <td>
                          {t.projectMilestoneName ? (
                            <span className="tag tag-info tag-dot">{t.projectMilestoneName}</span>
                          ) : (
                            <span className="xs muted">Not linked</span>
                          )}
                        </td>
                        <td>
                          <div className="flex gap-2 center">
                            <div className="user-avatar" style={{ width: 24, height: 24, fontSize: 10, background: 'var(--surface-3)', color: 'var(--ink)' }}>
                              {getOwnerInitials(t.owner)}
                            </div>
                            <span className="xs">{getOwnerName(t.owner)}</span>
                          </div>
                        </td>
                        <td className={overdue ? 'text-danger' : 'muted'}>
                          {t.dueAt ? fmtDate(t.dueAt) : '—'}
                          {overdue && <span className="tag tag-danger tag-dot tiny" style={{ marginLeft: 6 }}>Overdue</span>}
                        </td>
                        <td>
                          <span className={`tag tag-${priorityVariant} tag-dot`}>
                            {t.priority.charAt(0).toUpperCase() + t.priority.slice(1)}
                          </span>
                        </td>
                        <td>
                          <select 
                            className="btn btn-xs btn-ghost"
                            value={t.status} 
                            onChange={(e) => toggleTaskStatus(t, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="todo">Not started</option>
                            <option value="in_progress">In progress</option>
                            <option value="blocked">Blocked</option>
                            <option value="done">Done</option>
                          </select>
                        </td>
                        <td>
                          <i className="ti ti-chevron-right clickable-row-arrow" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

        </>
      )}

      <TaskCreateModal 
        open={showCreateTask} 
        onClose={() => setShowCreateTask(false)} 
        studyId={studyId}
        milestoneOptions={milestoneMetadataList}
        onCreated={syncProjectTrackingData}
      />

      <style>{`
        .clickable-row {
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .clickable-row:hover {
          background: var(--surface-2);
        }

        .clickable-row-arrow {
          color: var(--ink-3);
          font-size: 14px;
          opacity: 0.75;
        }

        .clickable-row:hover .clickable-row-arrow {
          opacity: 1;
          color: var(--ink);
        }
      `}</style>
    </div>
  );
}

export default ProjectTrackingModule;